import { keepPreviousData, useQuery, useQueries } from "@tanstack/react-query";

import type {
    BacklinksBacklinksCreatePayload,
    BacklinksBacklinksCreateParams,
    BacklinksRefdomainsCreateParams,
    BacklinksSummaryListParams,
    BacklinksTimeseriesListParams,
    BacklinksTimeseriesNewlostListParams,
    BacklinksRefdomainsCreatePayload,
    BacklinksTimeseriesListData,
} from "@similarweb/pro-api-client";
import Sentry from "@similarweb/sentry-logger";

import { apiClient } from "app/modules/api-client";

import type { BacklinksErrorResponse } from "./BacklinksTypes";

const backlinksApiClient = apiClient.Backlinks;

export const useBacklinksDomainApi = (
    queryRefDomains: BacklinksRefdomainsCreateParams,
    payload: BacklinksRefdomainsCreatePayload = [],
) => {
    return useQuery({
        queryKey: ["backlinks-refDomains", queryRefDomains, payload],
        queryFn: () => backlinksApiClient.backlinksRefdomainsCreate(queryRefDomains, payload),
        placeholderData: keepPreviousData,
        staleTime: Infinity,
        throwOnError: (err: BacklinksErrorResponse) => {
            Sentry.errorTracker({
                message: "useBacklinksDomainApi",
                e: new Error(err.Title, { cause: err }),
            });
            return false;
        },
    });
};

export const useBacklinksSummaryApi = (querySummary: BacklinksSummaryListParams = {}) => {
    return useQuery({
        queryKey: ["backlinks-summary", querySummary],
        queryFn: () => backlinksApiClient.backlinksSummaryList(querySummary),
        staleTime: Infinity,
        throwOnError: (err: BacklinksErrorResponse) => {
            Sentry.errorTracker({
                message: "useBacklinksSummaryApi",
                e: new Error(err.Title, { cause: err }),
            });
            return false;
        },
    });
};

export const useBacklinksApi = (
    querySummary: BacklinksBacklinksCreateParams = {},
    payload: BacklinksBacklinksCreatePayload = [],
) => {
    return useQuery({
        queryKey: ["backlinks-backlinks", querySummary, payload],
        queryFn: () => backlinksApiClient.backlinksBacklinksCreate(querySummary, payload),
        staleTime: Infinity,
        placeholderData: keepPreviousData,
        throwOnError: (err: BacklinksErrorResponse) => {
            Sentry.errorTracker({
                message: "useBacklinksApi",
                e: new Error(err.Title, { cause: err }),
            });
            return false;
        },
    });
};

export const useBacklinksTimeseriesApi = (querySummary: BacklinksTimeseriesListParams = {}) => {
    return useQuery({
        queryKey: ["backlinks-timeseries", querySummary],
        queryFn: () => backlinksApiClient.backlinksTimeseriesList(querySummary),
        staleTime: Infinity,
        placeholderData: keepPreviousData,
        throwOnError: (err: BacklinksErrorResponse) => {
            Sentry.errorTracker({
                message: "useBacklinksTimeseriesApi",
                e: new Error(err.Title, { cause: err }),
            });
            return false;
        },
    });
};

/**
 * Performs bulk time series API requests for multiple keys
 * @param keys - Array of string keys to fetch data for
 * @param baseQuerySummary - Base query parameters that will be used for all requests
 * @returns Object with query results mapped by key
 */
export const useBacklinksTimeseriesBulkApi = (
    keys: Array<string | undefined>,
    baseQuerySummary: BacklinksTimeseriesListParams = {},
) => {
    const cleanKeys = keys.filter(Boolean);

    return useQueries({
        queries: cleanKeys.map((key) => {
            // Create a new query object for each key by extending the base query parameters
            const querySummary: BacklinksTimeseriesListParams = {
                ...baseQuerySummary,
                key,
            };

            return {
                disabled: !key,
                queryKey: ["backlinks-timeseries", querySummary],
                queryFn: () => backlinksApiClient.backlinksTimeseriesList(querySummary),
                staleTime: Infinity,
                placeholderData: keepPreviousData,
                throwOnError: (err: BacklinksErrorResponse) => {
                    Sentry.errorTracker({
                        message: "useBacklinksTimeseriesBulkApi",
                        e: new Error(err.Title, { cause: err }),
                        params: {
                            extra: {
                                key,
                            },
                        },
                    });
                    return false;
                },
            };
        }),
        combine: (results) => {
            return {
                data: results.map((result) => result.data).filter(Boolean) as BacklinksTimeseriesListData[],
                isLoading: results.some((result) => result.isLoading || result.isFetching),
                isError: results.some((result) => result.isError),
            };
        },
    });
};

export const useBacklinksTimeseriesNewLostApi = (querySummary: BacklinksTimeseriesNewlostListParams = {}) => {
    return useQuery({
        queryKey: ["backlinks-timeseries-new-lost", querySummary],
        queryFn: () => backlinksApiClient.backlinksTimeseriesNewlostList(querySummary),
        staleTime: Infinity,
        placeholderData: keepPreviousData,
        throwOnError: (err: BacklinksErrorResponse) => {
            Sentry.errorTracker({
                message: "useBacklinksTimeseriesNewLostApi",
                e: new Error(err.Title, { cause: err }),
            });
            return false;
        },
    });
};
